import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Logo from "./Logo";
import { useI18n, LANGS } from "../i18n";
import { useUi } from "../lib/ui";
import { smoothScrollToId, smoothScrollToTop } from "../lib/scroll";

const SECTIONS = [
  { id: "sectores", key: "nav.sectors" },
  { id: "circuito", key: "nav.process" },
  { id: "transparencia", key: "nav.transparency" },
  { id: "panel", key: "nav.data" },
  { id: "preguntas", key: "nav.faq" },
];

export default function Header() {
  const { t, lang, path } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const { openContact } = useUi();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.pageYOffset > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const isHome = location.pathname === `/${lang}` || location.pathname === `/${lang}/`;

  const goToSection = (id) => {
    setOpen(false);
    if (isHome) {
      smoothScrollToId(id);
    } else {
      navigate(path(""), { state: { scrollTo: id } });
    }
  };

  const goHomeTop = (e) => {
    if (isHome) {
      e.preventDefault();
      smoothScrollToTop();
    }
    setOpen(false);
  };

  const switchLang = (target) => {
    if (target === lang) return;
    const rest = location.pathname.replace(/^\/(ar|en)/, "");
    setOpen(false);
    navigate(`/${target}${rest}${location.hash || ""}`);
  };

  return (
    <header className={`site-header${scrolled ? " is-scrolled" : ""}`}>
      <div className="container header-inner">
        <Link to={path("")} className="brand" onClick={goHomeTop} data-testid="brand-logo">
          <Logo />
          <span className="brand-text">
            <span className="brand-name">{t("brand.name")}</span>
            <span className="brand-tagline">{t("brand.tagline")}</span>
          </span>
        </Link>

        <nav className="nav-desktop" aria-label={t("nav.home")}>
          <Link to={path("")} className="nav-link" onClick={goHomeTop} data-testid="nav-home">
            {t("nav.home")}
          </Link>
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              type="button"
              className="nav-link"
              onClick={() => goToSection(s.id)}
              data-testid={`nav-${s.id}`}
            >
              {t(s.key)}
            </button>
          ))}
        </nav>

        <div className="header-actions">
          <div className="lang-switch" role="group" aria-label={t("lang.label")}>
            {LANGS.map((l) => (
              <button
                key={l}
                type="button"
                className={`lang-btn${l === lang ? " is-active" : ""}`}
                onClick={() => switchLang(l)}
                aria-pressed={l === lang}
                data-testid={`lang-${l}`}
              >
                {t(`lang.${l}Short`)}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="btn btn-primary btn-compact header-cta"
            onClick={openContact}
            data-testid="header-cta"
          >
            {t("common.requestReport")}
          </button>
          <button
            type="button"
            className="hamburger"
            aria-label={open ? t("nav.closeMenu") : t("nav.openMenu")}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            data-testid="hamburger"
          >
            <span className={`ham-bars${open ? " is-open" : ""}`} />
          </button>
        </div>
      </div>

      {open && (
        <div className="nav-mobile" data-testid="mobile-menu">
          <Link to={path("")} className="nav-mobile-link" onClick={goHomeTop} data-testid="m-nav-home">
            {t("nav.home")}
          </Link>
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              type="button"
              className="nav-mobile-link"
              onClick={() => goToSection(s.id)}
              data-testid={`m-nav-${s.id}`}
            >
              {t(s.key)}
            </button>
          ))}
          <button
            type="button"
            className="nav-mobile-link"
            onClick={() => goToSection("contacto")}
            data-testid="m-nav-contacto"
          >
            {t("nav.contact")}
          </button>
          <div className="nav-mobile-sep" />
          <Link to={path("/privacy")} className="nav-mobile-sub" data-testid="m-nav-privacy">
            {t("nav.privacy")}
          </Link>
          <Link to={path("/cookies")} className="nav-mobile-sub" data-testid="m-nav-cookies">
            {t("nav.cookies")}
          </Link>
          <Link to={path("/terms")} className="nav-mobile-sub" data-testid="m-nav-terms">
            {t("nav.terms")}
          </Link>
          <button
            type="button"
            className="btn btn-primary nav-mobile-cta"
            onClick={() => {
              setOpen(false);
              openContact();
            }}
            data-testid="m-header-cta"
          >
            {t("common.requestReport")}
          </button>
        </div>
      )}
    </header>
  );
}
